import { memo, useCallback, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";

import FGTooltipLabelView from "./FGTooltipLabelView";
import FGLabelValueList from "./FGLabelValueList";

import { postCall_v2 } from "services";
import { useToaster } from "hooks";
import { moneyFormat } from "utils";

import { JOMS_API_BASE_V1_URL, error } from "constants";

import {
  getFormattedPaymentRequest,
  getFormattedCreditRequest,
} from "../makeData";

const FGPaymentSummaryCard = ({ order }) => {
  const [toolTipData, setToolTipData] = useState({});
  const [isToolTipDataFetched, setIsToolTipDataFetched] = useState(false);
  const triggerToaster = useToaster();
  const paymentRequestBreakup = useMemo(
    () => toolTipData?.paymentRequestBreakupDTO || {},
    [toolTipData?.paymentRequestBreakupDTO],
  );
  const creditRequestBreakup = useMemo(
    () => toolTipData?.creditRequestBreakupDTO || {},
    [toolTipData?.creditRequestBreakupDTO],
  );
  const paymentRequestItems = useMemo(
    () => getFormattedPaymentRequest(paymentRequestBreakup),
    [paymentRequestBreakup],
  );
  const creditRequestItems = useMemo(
    () => getFormattedCreditRequest(creditRequestBreakup),
    [creditRequestBreakup],
  );

  const handlePaymentToolTip = useCallback(async () => {
    if (isToolTipDataFetched) return;
    try {
      const payload = {
        preDoIds:
          order?.preDoReleaseIds?.length > 0
            ? order.preDoReleaseIds.map(String)
            : [],
      };
      const { data } = await postCall_v2(
        `${JOMS_API_BASE_V1_URL}/v1/inventory-data/fetch-pre-do-balance-data/bulk`,
        payload,
      );
      setToolTipData(data);
      setIsToolTipDataFetched(true);
    } catch (err) {
      triggerToaster(
        err?.data?.errorMessage || "Please try again later",
        error,
      );
    }
  }, [isToolTipDataFetched, order?.preDoReleaseIds, triggerToaster]);
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "primary.light",
        borderRadius: 1.5,
        margin: 2,
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          padding: "12px 20px",
          borderColor: "primary.light",
          minWidth: "180px",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            color: "primary.main",
            fontSize: "12px",
            borderRight: "1px solid",
            pr: 5,
            py: 1,
          }}
        >
          ORDER PAYMENT SUMMARY
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "12px 2px",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
          <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
            Payment Request
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ fontWeight: 600, fontSize: "15px" }}>
              {moneyFormat(order?.paymentRequest)}
            </Typography>
            <FGTooltipLabelView
              type="Payment Request"
              onTooltipOpen={handlePaymentToolTip}
              tooltipContent={
                <FGLabelValueList layout="column" items={paymentRequestItems} />
              }
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
          <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
            Credit Request
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ fontWeight: 600, fontSize: "15px" }}>
              {moneyFormat(order?.creditRequest)}
            </Typography>
            <FGTooltipLabelView
              type="Credit Request"
              onTooltipOpen={handlePaymentToolTip}
              tooltipContent={
                <FGLabelValueList layout="column" items={creditRequestItems} />
              }
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

FGPaymentSummaryCard.propTypes = {
  order: PropTypes.shape({
    paymentRequest: PropTypes.number,
    creditRequest: PropTypes.number,
    preDoReleaseIds: PropTypes.arrayOf(PropTypes.number),
  }),
};

export default memo(FGPaymentSummaryCard);
